import { addDoc, collection, doc, onSnapshot, query, runTransaction, serverTimestamp, where } from "firebase/firestore";
import { auth, db } from "../firebase/firebase-config";

export const PUBLIC_UPGRADE_LINE_SCOPE = "__public_upgrade_lines__";
export const UPGRADE_LINE_ADMIN_UID = "o1TwhgGqQ7PjAr6EITaJtDBfBbp1";

// Scope markers select records. Firestore Rules authorize signed-in submission
// and restrict shared-record reads and mutations to the authenticated administrator.

const UPGRADE_LINES_COLLECTION = "upgrade-lines";
const INITIAL_LOAD_TIMEOUT_MS = 30000;
const isRecord = value => Boolean(value) && typeof value === "object" && !Array.isArray(value);

const invalid = (field, message) => Object.assign(new Error(message), {
  code: "UPGRADE_LINE_INVALID", field, fieldErrors: { [field]: message },
});
const readFailure = () => Object.assign(new Error("Upgrade lines could not be loaded. Check your connection and try again."), {
  code: "UPGRADE_LINE_READ_FAILED",
});
const requireOwner = userId => {
  if (typeof userId !== "string" || !userId.trim()) {
    throw Object.assign(new Error("Sign in to use Upgrade Line."), { code: "UPGRADE_LINE_AUTH_REQUIRED" });
  }
  return userId;
};
const requireSubmitter = () => {
  const user = auth.currentUser;
  if (!user || typeof user.uid !== "string" || !user.uid.trim() || user.uid.length > 128 || user.isAnonymous !== false) {
    throw Object.assign(new Error("Sign in with an account to submit an upgrade line."), { code: "UPGRADE_LINE_AUTH_REQUIRED" });
  }
  return user.uid;
};
const invalidRecordId = () => Object.assign(new Error("Choose a valid upgrade line. Refresh the list and try again."), {
  code: "UPGRADE_LINE_ID_INVALID",
});
const requireRecordId = recordId => {
  if (typeof recordId !== "string" || !recordId.trim() || recordId.includes("/")
    || recordId === "." || recordId === ".." || /^__.*__$/.test(recordId)) throw invalidRecordId();
  try {
    // Firestore document IDs contain one path segment of at most 1,500 UTF-8 bytes.
    if (encodeURIComponent(recordId).replace(/%[0-9A-F]{2}/g, "x").length > 1500) throw invalidRecordId();
  } catch { throw invalidRecordId(); }
  return recordId;
};
const recordUnavailable = () => Object.assign(new Error("This upgrade line is no longer available. Refresh the list and try again."), {
  code: "UPGRADE_LINE_NOT_FOUND",
});
const mutationFailure = action => Object.assign(new Error(`The upgrade line could not be ${action === "update" ? "updated" : "deleted"}. Check your connection and try again.`), {
  code: action === "update" ? "UPGRADE_LINE_UPDATE_FAILED" : "UPGRADE_LINE_DELETE_FAILED",
});

export const normalizeUpgradeLine = form => {
  const input = isRecord(form) ? form : {};
  const number = typeof input.number === "string" ? input.number.trim() : "";
  const digitCount = number.replace(/[^0-9]/g, "").length;
  if (!/^\+?[0-9 ()-]+$/.test(number) || number.length > 30 || digitCount < 7 || digitCount > 15) {
    throw invalid("number", "Enter a phone number with 7–15 digits, using only an optional leading +, spaces, parentheses, or hyphens (30 characters maximum).");
  }

  const lines = typeof input.noOfLines === "string" ? input.noOfLines.trim() : input.noOfLines;
  if ((typeof lines !== "number" && (typeof lines !== "string" || !/^[0-9]+$/.test(lines)))
    || !Number.isSafeInteger(Number(lines)) || Number(lines) < 1) {
    throw invalid("noOfLines", "Enter a whole number of lines greater than zero.");
  }
  const noOfLines = Number(lines);

  const amount = typeof input.lastPaid === "string" ? input.lastPaid.trim() : "";
  if (!/^(?:[0-9]+(?:\.[0-9]{1,2})?|\.[0-9]{1,2})$/.test(amount)) {
    throw invalid("lastPaid", "Enter a nonnegative last paid amount with up to two decimal places.");
  }
  const [whole, fraction = ""] = amount.split(".");
  // Join decimal digits before conversion to avoid floating point multiplication.
  const lastPaidCents = Number(`${whole || "0"}${fraction.padEnd(2, "0")}`);
  if (!Number.isSafeInteger(lastPaidCents) || lastPaidCents < 0) {
    throw invalid("lastPaid", "The last paid amount is too large. Enter a smaller amount.");
  }

  const remarks = input.remarks === undefined ? "" : input.remarks;
  if (typeof remarks !== "string" || remarks.trim().length > 2000) {
    throw invalid("remarks", "Keep remarks to 2,000 characters or fewer.");
  }
  return { number, noOfLines, lastPaidCents, remarks: remarks.trim() };
};

export const formatLastPaid = cents => {
  if (!Number.isSafeInteger(cents) || cents < 0) {
    throw invalid("lastPaid", "The saved last paid amount is invalid.");
  }
  const digits = String(cents).padStart(3, "0");
  return `${digits.slice(0, -2)}.${digits.slice(-2)}`;
};

export const createUpgradeLine = async (userId, form) => {
  const owner = requireOwner(userId);
  const submission = owner === PUBLIC_UPGRADE_LINE_SCOPE ? { submittedBy: requireSubmitter() } : {};
  const normalized = normalizeUpgradeLine(form);
  try {
    const saved = await addDoc(collection(db, UPGRADE_LINES_COLLECTION), {
      ...normalized, userId: owner, ...submission, createdAt: serverTimestamp(),
    });
    return { id: saved.id, ...normalized, userId: owner, ...submission };
  } catch {
    throw Object.assign(new Error("The upgrade line could not be saved. Check your connection and try again."), {
      code: "UPGRADE_LINE_WRITE_FAILED",
    });
  }
};

export const updateUpgradeLine = async (userId, recordId, form) => {
  const owner = requireOwner(userId);
  const id = requireRecordId(recordId);
  const normalized = normalizeUpgradeLine(form);
  try {
    const ref = doc(db, UPGRADE_LINES_COLLECTION, id);
    await runTransaction(db, async transaction => {
      const existing = await transaction.get(ref);
      if (!existing.exists() || existing.data()?.userId !== owner) throw recordUnavailable();
      // Update only editable fields so ownership and the original server time stay intact.
      transaction.update(ref, normalized);
    });
    return { id, ...normalized, userId: owner };
  } catch (error) {
    if (error?.code === "UPGRADE_LINE_NOT_FOUND") throw recordUnavailable();
    throw mutationFailure("update");
  }
};

export const deleteUpgradeLine = async (userId, recordId) => {
  const owner = requireOwner(userId);
  const id = requireRecordId(recordId);
  try {
    const ref = doc(db, UPGRADE_LINES_COLLECTION, id);
    await runTransaction(db, async transaction => {
      const existing = await transaction.get(ref);
      if (!existing.exists() || existing.data()?.userId !== owner) throw recordUnavailable();
      transaction.delete(ref);
    });
  } catch (error) {
    if (error?.code === "UPGRADE_LINE_NOT_FOUND") throw recordUnavailable();
    throw mutationFailure("delete");
  }
};

const readUpgradeLine = (snapshot, value, owner) => {
  if (!isRecord(value) || value.userId !== owner || typeof snapshot.id !== "string" || !snapshot.id
    || typeof value.noOfLines !== "number" || typeof value.remarks !== "string"
    || !value.createdAt || typeof value.createdAt.toDate !== "function") throw readFailure();
  const normalized = normalizeUpgradeLine({
    number: value.number, noOfLines: value.noOfLines, lastPaid: formatLastPaid(value.lastPaidCents), remarks: value.remarks,
  });
  const date = value.createdAt.toDate();
  if (!(date instanceof Date) || !Number.isFinite(date.getTime())) throw readFailure();
  const submission = {};
  if (owner === PUBLIC_UPGRADE_LINE_SCOPE && Object.prototype.hasOwnProperty.call(value, "submittedBy")) {
    if (typeof value.submittedBy !== "string" || !value.submittedBy.length || value.submittedBy.length > 128) throw readFailure();
    submission.submittedBy = value.submittedBy;
  }
  return { id: snapshot.id, ...normalized, userId: owner, ...submission, createdAt: date.toISOString() };
};

export const subscribeUpgradeLines = (userId, onChange, onError) => {
  const owner = requireOwner(userId);
  let stopped = false;
  let unsubscribe;
  const reportError = () => { if (!stopped && typeof onError === "function") onError(readFailure()); };
  const timeout = setTimeout(reportError, INITIAL_LOAD_TIMEOUT_MS);
  try {
    const ownedLines = query(collection(db, UPGRADE_LINES_COLLECTION), where("userId", "==", owner));
    unsubscribe = onSnapshot(ownedLines, { includeMetadataChanges: true }, snapshot => {
      // Cached rows and local writes cannot confirm a saved cloud record.
      if (stopped || snapshot.metadata?.fromCache || snapshot.metadata?.hasPendingWrites
        || snapshot.docs.some(row => row.metadata?.hasPendingWrites || row.metadata?.fromCache)) return;
      clearTimeout(timeout);
      let rows;
      try {
        rows = snapshot.docs.map(row => ({ snapshot: row, value: row.data() }))
          .filter(row => row.value?.userId === owner)
          .map(row => readUpgradeLine(row.snapshot, row.value, owner))
          .sort((left, right) => right.createdAt.localeCompare(left.createdAt) || left.id.localeCompare(right.id));
      } catch {
        reportError();
        return;
      }
      onChange(rows);
    }, () => { clearTimeout(timeout); reportError(); });
  } catch {
    clearTimeout(timeout);
    reportError();
  }
  return () => {
    if (stopped) return;
    stopped = true;
    clearTimeout(timeout);
    if (typeof unsubscribe === "function") unsubscribe();
  };
};
